import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchActors, searchDramas, sortDramas } from "@/lib/data";
import { dramas } from "@/mocks/dramas";
import type { Drama } from "@/types";
import PageHeader from "@/components/base/PageHeader";
import DramaCard from "@/components/base/DramaCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";

const recommended = sortDramas(dramas as Drama[], "popularity").slice(0, 4);

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const [keyword, setKeyword] = useState(query);
  const [tab, setTab] = useState<"all" | "drama" | "actor">("all");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta(
    query ? `“${query}” 搜索结果 - 漂亮的保姆` : "搜索 - 漂亮的保姆",
    query
      ? `在漂亮的保姆搜索“${query}”的相关电视剧与演员信息，查看剧情介绍、演员资料与最新影视动态。`
      : "在漂亮的保姆搜索电视剧与演员，快速找到剧情介绍与演员资料。",
    "漂亮的保姆,搜索,电视剧搜索,演员搜索,剧情介绍",
  );

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const kw = keyword.trim();
    setParams(kw ? { q: kw } : {});
    setTab("all");
  }

  const dramaResults = query ? searchDramas(query) : [];
  const actorResults = query ? searchActors(query) : [];
  const totalResults = dramaResults.length + actorResults.length;

  return (
    <>
      <PageHeader
        title="站内搜索"
        desc="输入剧集名称、演员姓名或关键词，快速查找剧情介绍与演员资料。"
        crumbs={[{ label: "首页", to: "/" }, { label: "搜索" }]}
      />

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="试试搜索：长安烟雨录 / 林清月 / 悬疑"
              aria-label="搜索剧集或演员"
              className="w-full h-12 pl-11 pr-4 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>

        {query && (
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {[
              { key: "all" as const, label: `全部 (${totalResults})` },
              { key: "drama" as const, label: `电视剧 (${dramaResults.length})` },
              { key: "actor" as const, label: `演员 (${actorResults.length})` },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-4 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                  tab === t.key
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {!query && (
          <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 mb-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
              热门搜索
            </h2>
            <div className="flex flex-wrap gap-2">
              {["长安烟雨录", "都市迷航", "悬疑", "林清月", "顾南亭", "古装", "爱情"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setKeyword(k);
                    setParams({ q: k });
                    setTab("all");
                  }}
                  className="px-3.5 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {k}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-foreground-400">
              提示：搜索结果由前端演示数据生成，未调用真实接口。
            </p>
          </div>
        )}

        {query && totalResults === 0 && (
          <>
            <EmptyState
              title={`没有找到与“${query}”相关的内容`}
              desc="换个关键词试试，或者看看下面的热门推荐。"
              actionText="清空搜索"
              onAction={() => {
                setParams({});
                setKeyword("");
              }}
            />
            <div className="mt-10">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                为你推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {recommended.map((d) => (
                  <DramaCard key={d.id} drama={d} />
                ))}
              </div>
            </div>
          </>
        )}

        {query && totalResults > 0 && (
          <div className="space-y-10">
            {(tab === "all" || tab === "drama") && dramaResults.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  电视剧（{dramaResults.length}）
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {dramaResults.map((d) => (
                    <DramaCard key={d.id} drama={d} />
                  ))}
                </div>
              </section>
            )}

            {(tab === "all" || tab === "actor") && actorResults.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  演员（{actorResults.length}）
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {actorResults.map((a) => (
                    <Link
                      key={a.id}
                      to={`/actor/${a.id}`}
                      className="group flex gap-4 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={a.avatar}
                          alt={a.name}
                          title={`${a.name} - 漂亮的保姆`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                          {a.name}
                        </p>
                        <p className="text-xs text-foreground-400 mt-1">
                          {a.region} · {a.height}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                          {a.bio}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </>
  );
}